
import { useQueryClient } from '@tanstack/react-query';
import { BaseAttachedResearch } from '@/types/research-attachments';
import { getEntityConfig } from './entity-configs';
import { ResearchAttachmentOperations } from './types/operations';
import {
  fetchAttachments,
  attachResearch,
  detachResearch,
  updateSummary
} from './operations';

export const useResearchAttachmentOperations = <T extends BaseAttachedResearch = BaseAttachedResearch>(
  entityType: string
): ResearchAttachmentOperations<T> => {
  const config = getEntityConfig(entityType);
  const queryClient = useQueryClient();

  const fetchAttachmentsWrapper = async (entityId: string): Promise<T[]> => {
    return fetchAttachments<T>(config, entityId);
  };

  const attachResearchWrapper = async (entityId: string, researchId: string): Promise<T> => {
    return attachResearch<T>(config, entityId, researchId);
  };

  const detachResearchWrapper = async (attachmentId: string, entityId: string): Promise<void> => {
    await detachResearch(config, attachmentId);
    
    // Invalidate queries for this entity
    queryClient.invalidateQueries({ 
      queryKey: [...config.queryKey, entityId] 
    });
  };

  const updateSummaryWrapper = async (attachmentId: string, summary: string, entityId: string): Promise<T> => {
    const result = await updateSummary<T>(config, attachmentId, summary);
    
    // Invalidate queries for this entity
    queryClient.invalidateQueries({ 
      queryKey: [...config.queryKey, entityId] 
    });

    return result;
  };

  return {
    fetchAttachments: fetchAttachmentsWrapper,
    attachResearch: attachResearchWrapper,
    detachResearch: detachResearchWrapper,
    updateSummary: updateSummaryWrapper,
    entityConfig: config
  };
};
